import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface CloneTemplateDialogProps {
  templateId: string;
  templateName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CloneTemplateDialog({
  templateId,
  templateName,
  open,
  onOpenChange,
}: CloneTemplateDialogProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [excludeLocation, setExcludeLocation] = useState(false);

  useEffect(() => {
    if (open) {
      setName(`${templateName} (Clone)`);
      setExcludeLocation(false);
    }
  }, [open, templateName]);

  const cloneMutation = useMutation({
    mutationFn: async (data: { name: string; excludeLocationChallenges: boolean }) => {
      return apiClient.post(`/templates/${templateId}/duplicate`, data) as Promise<{
        success: boolean;
        data: { id: string };
      }>;
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['Template'] });
      toast.success('Template cloned successfully');
      onOpenChange(false);
      navigate(`/templates/${response.data.id}/overview`);
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to clone template');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    cloneMutation.mutate({
      name: name.trim(),
      excludeLocationChallenges: excludeLocation,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Clone Template</DialogTitle>
            <DialogDescription>
              Create a copy of this template with all its challenges and groups.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="clone-name">Template Name</Label>
              <Input
                id="clone-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter a name for the cloned template"
                required
              />
            </div>

            <div className="flex items-start gap-2.5">
              <Checkbox
                id="exclude-location"
                checked={excludeLocation}
                onCheckedChange={(checked) => setExcludeLocation(checked === true)}
              />
              <div className="space-y-1">
                <Label htmlFor="exclude-location" className="text-sm font-medium cursor-pointer">
                  Exclude location-based challenges
                </Label>
                <p className="text-xs text-muted-foreground">
                  Remove challenges with GPS coordinates (useful when cloning for a different city)
                </p>
              </div>
            </div>

            {excludeLocation && (
              <div className="flex gap-2.5 rounded-lg border border-blue-200 bg-blue-50 p-3">
                <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                <p className="text-sm text-blue-800">
                  Location-based challenges will be excluded. You can add new location challenges
                  for your target city after cloning.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={cloneMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || cloneMutation.isPending}>
              {cloneMutation.isPending ? 'Cloning...' : 'Clone Template'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
